<template>
  <v-dialog
    v-model="paymentDialog"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    max-width="60%"
    persistent
  >
    <v-card :loading="isFinalizing && gettingFinalizingData">
      <v-toolbar
        dark
        color="secondary"
        class="elevation-0"
      >
        <v-btn
          icon
          dark
          @click.native="paymentDialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ isFinalizing ? "סגירת הזמנה" : "חיוב / זיכוי" }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          :color="editable.lock ? `white` : `secondary darken-2`"
          icon
          @click="editable.lock = !editable.lock"
          class="me-2"
        >
          <v-icon>mdi-lock</v-icon>
        </v-btn>
        <v-btn
          color="secondary darken-2"
          depressed
          large
          :loading="loading"
          :disabled="loading || editable.lock"
          @click="makePayment"
        >
          ביצוע חיוב
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>פריט</th>
              <th width="150px">מחיר</th>
              <th width="80px">מע״מ</th>
              <th
                width="80px"
                class="text-end"
              >מחיקה</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in editable.items"
              :key="'item' + index"
            >
              <td>
                <v-text-field v-model="item.title"></v-text-field>
              </td>
              <td>
                <v-text-field
                  v-model="item.total"
                  type="number"
                  dir="ltr"
                  suffix="₪"
                ></v-text-field>
              </td>
              <td>
                <v-checkbox
                  v-model="item.vat"
                  color="primary"
                ></v-checkbox>
              </td>
              <td class="text-end">
                <v-btn
                  icon
                  small
                  text
                  color="error"
                  @click="removeItem(index)"
                  class="ma-0"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td class="title">סה״כ</td>
              <td
                class="title"
                dir="ltr"
              >₪{{ total }}</td>
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        </v-simple-table>
      </v-card-text>
      <v-card-actions>
        <v-btn
          @click="addItem"
          color="primary"
          text
        >
          <v-icon>mdi-plus</v-icon>
          הוספת פריט
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import BigNumber from "bignumber.js";
import { getApi } from "@/api";
import Mixins from "@/mixins/mixins";
import { Promise } from "q";

export default {
  data() {
    return {
      loading: false,
      gettingFinalizingData: false,
      bill: null,
      prevPayments: [],
      editable: {}
    };
  },

  computed: {
    paymentDialog: {
      get() {
        return this.$store.state.paymentDialog;
      },
      set(isActive) {
        this.$store.commit("TOGGLE_PAYMENT_DIALOG", isActive);
      }
    },
    order() {
      return this.$store.state.entities.orders[this.orderId];
    },
    items() {
      if (this.editable.items) {
        return this.editable.items.map(item => {
          return {
            title: item.title,
            total: item.total,
            vat: item.vat
          };
        });
      }
      return [];
    },
    total() {
      let items = this.items;
      let total = BigNumber(0);
      for (let item of items) {
        let itemTotal = BigNumber(item.total);
        total = total.plus(itemTotal);
      }
      return total.toFormat(2);
    }
  },

  methods: {
    makePayment() {
      this.loading = true;

      getApi()
        .then(api => {
          let params = {
            items: this.items
          };
          return api.post(`/orders/${this.orderId}/payment`, params);
        })
        .then(() => {
          this.paymentDialog = false;
          this.$store.dispatch("SHOW_SNACKBAR", {
            text: "החיוב / זיכוי בוצעו בהצלחה",
            color: "success"
          });
        })
        .catch(err => {
          this.showError(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    initEditableResource() {
      this.editable = {
        items: [],
        lock: true
      };
    },
    resetEditableResource() {
      this.editable = {
        items: [this.generateItem()],
        lock: true
      };
    },
    generateItem(title = "", total = 0, vat = true) {
      return { title, total, vat };
    },
    addItem() {
      this.editable.items.push(this.generateItem());
    },
    removeItem(index) {
      this.$root.$confirm("האם למחוק את הפריט?").then(res => {
        if (!res) {
          return;
        }
        this.editable.items.splice(index, 1);
      });
    },
    getFinalizingData() {
      this.gettingFinalizingData = true;
      let query = {
        order: this.orderId
      };
      let params = { q: JSON.stringify(query), limit: 0 };
      getApi()
        .then(api => {
          return Promise.all([
            api.get(`/orders/${this.orderId}/bill`),
            api.get(`/payments`, { params })
          ]);
        })
        .then(array => {
          this.bill = array[0].data;
          this.prevPayments = array[1].data.results;

          let totalWithVat = this.prevPayments
            .map(item => item.totalWithVat)
            .reduce((p, c) => p + c, 0);
          let totalWithoutVat = this.prevPayments
            .map(item => item.totalWithoutVat)
            .reduce((p, c) => p + c, 0);

          this.initEditableResource();

          this.editable.items.push(
            this.generateItem(
              "חיובים קודמים - שירות לוויני חו״ל",
              BigNumber(totalWithoutVat / 100)
                .times(-1)
                .dp(2)
                .toNumber(),
              false
            )
          );

          this.editable.items.push(
            this.generateItem(
              "חיובים קודמים",
              BigNumber(totalWithVat / 100)
                .times(-1)
                .dp(2)
                .toNumber(),
              true
            )
          );

          for (let item of this.bill) {
            this.editable.items.push(
              this.generateItem(
                item.title,
                BigNumber(item.total)
                  .dp(2)
                  .toNumber(),
                BigNumber(item.totalBeforeVat)
                  .dp(2)
                  .toNumber() !=
                  BigNumber(item.total)
                    .dp(2)
                    .toNumber()
              )
            );
          }
        })
        .catch(err => {
          this.showError(err);
        })
        .finally(() => {
          this.gettingFinalizingData = false;
        });
    }
  },

  mounted() {
    this.resetEditableResource();
  },

  props: ["orderId", "isFinalizing"],

  mixins: [Mixins],

  watch: {
    paymentDialog: {
      handler: function(val) {
        if (!val) {
          this.resetEditableResource();
        } else {
          if (this.isFinalizing) {
            this.getFinalizingData();
          }
        }
      }
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
